import React from 'react';
import { Building, FileText, Package, Network } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

const BanfTypeSelector = ({ selectedType, onTypeChange }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const typeConfig = {
    kostenstelle: {
      icon: Building,
      label: t('banf.type.costCenter'),
      colors: {
        active: isDarkMode
          ? 'linear-gradient(135deg, rgba(16,185,129,0.8) 0%, rgba(5,150,105,0.8) 50%, rgba(4,120,87,0.8) 100%)'
          : '#059669',
        inactive: isDarkMode
          ? 'linear-gradient(135deg, rgba(16,185,129,0.4) 0%, rgba(5,150,105,0.4) 50%, rgba(4,120,87,0.4) 100%)'
          : 'transparent',
        shadow: {
          active: isDarkMode
            ? '0 8px 32px rgba(16,185,129,0.6), inset 0 1px 0 rgba(255,255,255,0.2)'
            : 'none',
          inactive: isDarkMode
            ? '0 4px 16px rgba(16,185,129,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'none'
        },
        ring: isDarkMode ? 'ring-green-400' : 'ring-emerald-500'
      }
    },
    auftrag: {
      icon: FileText,
      label: t('banf.type.order'),
      colors: {
        active: isDarkMode
          ? 'linear-gradient(135deg, rgba(147,51,234,0.8) 0%, rgba(126,34,206,0.8) 50%, rgba(107,33,168,0.8) 100%)'
          : '#7c3aed',
        inactive: isDarkMode
          ? 'linear-gradient(135deg, rgba(147,51,234,0.4) 0%, rgba(126,34,206,0.4) 50%, rgba(107,33,168,0.4) 100%)'
          : 'transparent',
        shadow: {
          active: isDarkMode
            ? '0 8px 32px rgba(147,51,234,0.6), inset 0 1px 0 rgba(255,255,255,0.2)'
            : 'none',
          inactive: isDarkMode
            ? '0 4px 16px rgba(147,51,234,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'none'
        },
        ring: isDarkMode ? 'ring-purple-400' : 'ring-violet-500'
      }
    },
    psp: {
      icon: Network,
      label: t('banf.type.psp'),
      colors: {
        active: isDarkMode
          ? 'linear-gradient(135deg, rgba(245,158,11,0.8) 0%, rgba(217,119,6,0.8) 50%, rgba(180,83,9,0.8) 100%)'
          : '#d97706',
        inactive: isDarkMode
          ? 'linear-gradient(135deg, rgba(245,158,11,0.4) 0%, rgba(217,119,6,0.4) 50%, rgba(180,83,9,0.4) 100%)'
          : 'transparent',
        shadow: {
          active: isDarkMode
            ? '0 8px 32px rgba(245,158,11,0.6), inset 0 1px 0 rgba(255,255,255,0.2)'
            : 'none',
          inactive: isDarkMode
            ? '0 4px 16px rgba(245,158,11,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'none'
        },
        ring: isDarkMode ? 'ring-amber-400' : 'ring-amber-500'
      }
    },
    material: {
      icon: Package,
      label: t('banf.type.material'),
      colors: {
        active: isDarkMode
          ? 'linear-gradient(135deg, rgba(59,130,246,0.8) 0%, rgba(37,99,235,0.8) 50%, rgba(29,78,216,0.8) 100%)'
          : '#2563EB',
        inactive: isDarkMode
          ? 'linear-gradient(135deg, rgba(59,130,246,0.4) 0%, rgba(37,99,235,0.4) 50%, rgba(29,78,216,0.4) 100%)'
          : 'transparent',
        shadow: {
          active: isDarkMode
            ? '0 8px 32px rgba(59,130,246,0.6), inset 0 1px 0 rgba(255,255,255,0.2)'
            : 'none',
          inactive: isDarkMode
            ? '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
            : 'none'
        },
        ring: 'ring-brand'
      }
    }
  };

  return (
    <div className="flex items-center space-x-3">
      {Object.entries(typeConfig).map(([type, config]) => {
        const Icon = config.icon;
        const isActive = selectedType === type;
        
        return (
          <button
            key={type}
            onClick={() => onTypeChange(type)}
            className={`relative group/${type}Btn overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${isActive ? `ring-2 ${config.colors.ring}` : ''} ${
              isDarkMode ? '' : (isActive ? 'text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200')
            }`}
            title={config.label}
            style={{
              background: isDarkMode ? (isActive ? config.colors.active : config.colors.inactive) : (isActive ? config.colors.active : undefined),
              backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
              border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
              boxShadow: isDarkMode ? (isActive ? config.colors.shadow.active : config.colors.shadow.inactive) : undefined
            }}
          >
            <div className={`relative z-10 px-4 py-2 flex items-center font-medium text-sm transition-all duration-300 ${
              isDarkMode ? 'text-white drop-shadow-lg' : (isActive ? 'text-white' : 'text-slate-700')
            }`}>
              <Icon className="w-4 h-4 mr-2" />
              <span>{config.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default BanfTypeSelector;